/**
 * Layout dropdown: current layout label, saved layouts (load/delete), save as, reset to default.
 */

import React, { useState } from 'react';
import { LayoutGrid, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import type { LayoutState } from './types';
import {
  loadLayout,
  saveLayout,
  listSavedLayouts,
  deleteLayout,
  resetToDefault,
  getActiveLayoutName,
  setActiveLayoutName,
} from '@/hooks/useActivityLayout';

export interface LayoutButtonProps {
  scope: string | undefined;
  currentState: LayoutState;
  onApplyLayout: (state: LayoutState) => void;
}

export function LayoutButton({ scope, currentState, onApplyLayout }: LayoutButtonProps) {
  const [saveName, setSaveName] = useState('');
  const [resetConfirm, setResetConfirm] = useState(false);

  const activeName = scope ? getActiveLayoutName(scope) : 'auto';
  const saved = scope ? listSavedLayouts(scope) : [];

  const handleLoad = (name: string) => {
    if (!scope) return;
    const state = loadLayout(scope, name);
    if (state) {
      onApplyLayout(state);
      setActiveLayoutName(scope, name);
    }
  };

  const handleSaveAs = () => {
    if (!scope || !saveName.trim()) return;
    const name = saveName.trim();
    saveLayout(scope, currentState, name);
    setActiveLayoutName(scope, name);
    setSaveName('');
  };

  const handleDelete = (e: React.MouseEvent, name: string) => {
    e.stopPropagation();
    if (!scope) return;
    deleteLayout(scope, name);
  };

  const handleReset = () => {
    if (!resetConfirm) {
      setResetConfirm(true);
      return;
    }
    const state = resetToDefault(scope);
    onApplyLayout(state);
    if (scope) setActiveLayoutName(scope, 'auto');
    setResetConfirm(false);
  };

  return (
    <DropdownMenu onOpenChange={(open) => { if (!open) setResetConfirm(false); }}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={!scope}>
          <LayoutGrid className="h-3.5 w-3.5 mr-1.5" />
          Layout
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-muted-foreground font-normal">
          {activeName === 'auto' ? 'Auto-saved' : activeName}
        </DropdownMenuLabel>
        {saved.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {saved.map((name) => (
              <DropdownMenuItem
                key={name}
                onSelect={(e) => { e.preventDefault(); handleLoad(name); }}
                className="flex items-center justify-between gap-2"
              >
                <span className="truncate">{name}</span>
                <button
                  type="button"
                  onClick={(e) => handleDelete(e, name)}
                  className="p-0.5 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive"
                  aria-label={`Delete ${name}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </DropdownMenuItem>
            ))}
          </>
        )}
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 flex gap-2 items-center">
          <Input
            placeholder="Save as..."
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSaveAs()}
            className="h-8 text-xs flex-1 min-w-0"
          />
          <Button size="sm" variant="secondary" className="h-8 text-xs shrink-0" onClick={handleSaveAs} disabled={!saveName.trim()}>
            Save
          </Button>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); handleReset(); }}
          className={resetConfirm ? 'text-destructive focus:text-destructive' : ''}
        >
          {resetConfirm ? 'Click again to reset' : 'Reset to default'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
